import { ErrorState } from "$components/error-state";
import { LoadingLogo } from "$components/loading/loading-logo";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider/provider";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { useInsuranceProvidersQuery } from "$routes/patient/[:patientId]/_lib/invoices/__generated__/TabContentOnline.gql";
import type { Insurer } from "$routes/patient/[:patientId]/_lib/invoices/components/InvoiceCard/InsurerAccordion/types";
import InvoicingEmptyState from "$routes/patient/[:patientId]/_lib/invoices/components/InvoicingEmptyState";
import { capitaliseDependentsEnum } from "$routes/patient/[:patientId]/_lib/invoices/components/insurers";
// PNE-328: the rxdb-billing path reads the price list from RxDB end-to-end.
// `useLocalPriceList` was already used by the (transitional) priceListRxdb
// path in TabContentRxdb; importing it here means both flag-on branches
// share the same offline-first source instead of V2 calling out to the
// Apollo `useFetchPriceListV2`. Follow-up: extract this hook into its own
// module under `invoices-v2/` once TabContentRxdb is decommissioned.
import { useLocalPriceList } from "$routes/patient/[:patientId]/_lib/invoices/TabContentRxdb";
import { useFetchPatientInvoicesRxdb } from "$routes/patient/[:patientId]/_lib/invoices-v2/use-fetch-patient-invoices-rxdb";
import { Box } from "@material-ui/core";
import { addDays, isAfter, isBefore } from "date-fns";
import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { NewInvoiceAction } from "./new-invoice-action";
import { splitOutstandingPrevious } from "./outstanding-partition";
import { OutstandingSection } from "./outstanding-section";
import { PreviousSection } from "./previous-section";
import type { V2InvoiceCardProps } from "./tab-content-types";

export const TabContentV2: FC<{ patientId: string }> = ({ patientId }) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();

  const { facilityId } = useUser();
  const { priceList } = useLocalPriceList(facilityId);
  const { data: insurersData, loading: insurersLoading } =
    useInsuranceProvidersQuery({
      fetchPolicy: "cache-and-network",
      onError() {
        toast.error(
          formatMessage({
            id: "invoicing.getInsurers.error.msg",
            defaultMessage: "Failed to fetch the insurers for this territory",
          }),
        );
      },
    });
  const { invoices, loading, error } = useFetchPatientInvoicesRxdb(patientId);

  const { fromDate, toDate } = useQuerystring() as {
    fromDate?: string;
    toDate?: string;
  };

  const setQueryParam = useCallback(
    (key: string) => (value: string) => {
      const params = new URLSearchParams(location.search);
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  const insurers = useMemo(
    // `properties` arrives from api-insurance as JsonObject — the strict
    // `Insurer[]` shape is satisfied at runtime, but the type-level cast at
    // this single boundary is the same one TabContentOnline does.
    () =>
      capitaliseDependentsEnum(
        insurersData?.minInsuranceProviders ?? [],
      ) as unknown as Insurer[],
    [insurersData],
  );

  const cards: V2InvoiceCardProps[] = useMemo(
    () =>
      invoices.map((invoice) => ({
        ...invoice,
        priceList,
        insurers,
        balance: invoice.invoiceSummary.balance,
      })),
    [invoices, priceList, insurers],
  );

  const { outstanding, previousAll } = useMemo(() => {
    const { outstanding: o, previous: p } = splitOutstandingPrevious(cards);
    return { outstanding: o, previousAll: p };
  }, [cards]);

  // Date filter applies to Previous only — Outstanding is always actionable.
  const previousFiltered = useMemo(() => {
    return previousAll.filter((invoice) => {
      const created = new Date(invoice.invoiceSummary.created);
      if (fromDate && isBefore(created, new Date(fromDate))) return false;
      if (toDate && isAfter(created, addDays(new Date(toDate), 1)))
        return false;
      return true;
    });
  }, [previousAll, fromDate, toDate]);

  if ((loading && invoices.length === 0) || insurersLoading)
    return <LoadingLogo />;

  if (error)
    return (
      <Box p={3}>
        <ErrorState error={error} />
      </Box>
    );

  if (!loading && invoices.length === 0) {
    return <InvoicingEmptyState />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      style={{ gap: 16 }}
      data-testid="tab-content-v2"
    >
      <NewInvoiceAction />
      <OutstandingSection invoices={outstanding} />
      <PreviousSection
        invoices={previousFiltered}
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setQueryParam("fromDate")}
        setToDate={setQueryParam("toDate")}
      />
    </Box>
  );
};
